import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	ListItemText,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
	ExpiryDashboardParams,
	ExpiryDashboardResponse,
	ExpiryDashboardRow,
	ExpiryStatus,
	fetchExpiryDashboard,
	sendExpiryNotification,
} from "../../api/dashboard";
import { CHART_COLORS, ChartCard, StackedBarChart } from "./DashboardCharts";
import { ConfirmButton } from "../shared/ConfirmButton";
import { formatDate, useDebouncedValue } from "./LettingDashboard";


const STATUS_SEGMENTS: { status: ExpiryStatus; label: string; color: string }[] = [
	{ status: "sent", label: "Sent", color: CHART_COLORS.success },
	{ status: "missed", label: "Missed", color: CHART_COLORS.error },
	{ status: "skipped", label: "Skipped (inactive / construction completed)", color: CHART_COLORS.neutral },
	{ status: "upcoming", label: "Not expired yet", color: CHART_COLORS.primary },
];

const NotificationSentCell: FC<{ row: ExpiryDashboardRow }> = ({ row }) => {
	switch (row.status) {
		case "sent":
			return <CheckCircleIcon fontSize="small" sx={{ color: CHART_COLORS.success }} />;
		case "missed":
			return <CancelIcon fontSize="small" sx={{ color: CHART_COLORS.error }} />;
		case "skipped":
			return (
				<Tooltip title="The scheduler skips inactive (E93) and construction completed (N66) projects">
					<Chip size="small" label="Skipped" />
				</Tooltip>
			);
		default:
			return <Typography fontSize={13} color="text.secondary">Not expired</Typography>;
	}
};

// Initial load shows records that expired in the last 2 years (up to today)
const DEFAULT_RANGE_YEARS = 2;
const defaultStartDate = () => dayjs().subtract(DEFAULT_RANGE_YEARS, "year").startOf("day");
const defaultEndDate = () => dayjs().startOf("day");

export const ExpiryDashboard: FC = () => {
	const navigate = useNavigate();
	const [status, setStatus] = useState<ExpiryDashboardParams["status"]>("");
	
    // empty = all types; the available types come from the backend
	const [types, setTypes] = useState<string[]>([]);
	const [startDate, setStartDate] = useState<Dayjs | null>(defaultStartDate);
	const [endDate, setEndDate] = useState<Dayjs | null>(defaultEndDate);

    const [searchInput, setSearchInput] = useState("");
	const search = useDebouncedValue(searchInput);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [data, setData] = useState<ExpiryDashboardResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [reloadKey, setReloadKey] = useState(0);

	const invalidRange = !!startDate && !!endDate && endDate.isBefore(startDate, "day");
    const isDefaultRange =
		!!startDate && !!endDate && startDate.isSame(defaultStartDate(), "day") && endDate.isSame(defaultEndDate(), "day");
	const availableTypes = data?.types ?? [];

	useEffect(() => {
		if (invalidRange) return;
		setLoading(true);
		setError("");
		fetchExpiryDashboard({
			types,
			start: startDate?.format("YYYY-MM-DD"),
			end: endDate?.format("YYYY-MM-DD"),
			status,
			search,
			page,
			page_size: pageSize,
		})
			.then(setData)
			.catch((e) => {
				console.error(e);
				setError("Unable to load the expiration dashboard.");
			})
			.finally(() => setLoading(false));
	}, [types, startDate, endDate, status, search, page, pageSize, reloadKey]);

	const withPageReset = <T,>(setter: (value: T) => void) => (value: T) => {
		setter(value);
		setPage(0);
	};

	const handleSend = (row: ExpiryDashboardRow) => {
		setMessage("");
		sendExpiryNotification(row.id)
			.then(() => {
				setMessage(`Expiry notification sent for ESR #${row.display_id} (${row.type}).`);
				setReloadKey((k) => k + 1);
			})
			.catch((e) => {
				console.error(e);
				setError(e?.response?.data?.detail ?? "Unable to send the expiry notification.");
			});
	};

	return (
		<Box>
			<Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 3 }}>
				<TextField
					select
					size="small"
					label="Expiry Type"
					sx={{ minWidth: 220 }}
					SelectProps={{
						multiple: true,
                        displayEmpty: true,
						renderValue: (selected) =>
							(selected as string[]).length === 0 || (selected as string[]).length === availableTypes.length
								? "All"
								: (selected as string[]).join(", "),
					}}
                    InputLabelProps={{ shrink: true }}
					value={types}
					onChange={(e) => {
						const value = e.target.value as unknown as string[];
						withPageReset(setTypes)(typeof value === "string" ? (value as string).split(",") : value);
					}}
				>
					{availableTypes.map((type) => (
						<MenuItem key={type} value={type}>
							<Checkbox size="small" checked={types.includes(type)} />
							<ListItemText primary={type} />
						</MenuItem>
					))}
				</TextField>
				<DatePicker
					label="Expiry from"
					value={startDate}
					onChange={withPageReset(setStartDate)}
					slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
				/>
				<DatePicker
					label="Expiry to"
					value={endDate}
					minDate={startDate ?? undefined}
					onChange={withPageReset(setEndDate)}
					slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
				/>
				<TextField
					select
					size="small"
					label="Notification"
					value={status}
					onChange={(e) => withPageReset(setStatus)(e.target.value as ExpiryDashboardParams["status"])}
					sx={{ minWidth: 170 }}
				>
					<MenuItem value="">All</MenuItem>
					{STATUS_SEGMENTS.map((s) => (
						<MenuItem key={s.status} value={s.status}>
							{s.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					label="Search ESR #"
					value={searchInput}
					onChange={(e) => withPageReset(setSearchInput)(e.target.value)}
				/>
                {!isDefaultRange && (
					<Button
						size="small"
						onClick={() => {
							setStartDate(defaultStartDate());
							setEndDate(defaultEndDate());
							setPage(0);
						}}
					>
						Reset to last {DEFAULT_RANGE_YEARS} years
					</Button>
				)}
			</Stack>
            <Alert severity="error" sx={{ mb: 2 }}>
				{isDefaultRange
					? `Showing records that expired in the last ${DEFAULT_RANGE_YEARS} years (${startDate?.format("MM/DD/YYYY")} – ${endDate?.format("MM/DD/YYYY")}). Change "Expiry from" / "Expiry to" to see a different range.`
					: !startDate && !endDate
						? "Showing all expiry records (no expiry date range selected)."
						: `Showing records with expiry dates ${startDate ? `from ${startDate.format("MM/DD/YYYY")}` : ""} ${endDate ? `to ${endDate.format("MM/DD/YYYY")}` : "onwards"}.`}
			</Alert>
			{invalidRange && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					End date must be on or after the start date.
				</Alert>
			)}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
					{error}
				</Alert>
			)}
			{message && (
				<Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage("")}>
					{message}
				</Alert>
			)}

			<Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
				<ChartCard title="Notification Health">
					<StackedBarChart
						rows={Object.entries(data?.health ?? {}).map(([type, counts]) => ({
							label: type,
							segments: STATUS_SEGMENTS.map((s) => ({
								label: s.label,
								value: counts[s.status] ?? 0,
								color: s.color,
							})),
						}))}
					/>
				</ChartCard>
			</Stack>

			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>ESR</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Actual Date</TableCell>
							<TableCell>Expiry Date</TableCell>
							<TableCell>Notification Sent</TableCell>
							<TableCell>Expired Days</TableCell>
							<TableCell>Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									<CircularProgress size={24} />
								</TableCell>
							</TableRow>
						) : (data?.rows ?? []).length === 0 ? (
							<TableRow>
								<TableCell colSpan={7}>
									<Typography color="text.secondary">No expiry records found for the selected filters.</Typography>
								</TableCell>
							</TableRow>
						) : (
							data?.rows.map((row) => (
								<TableRow key={row.id} hover>
									<TableCell>{row.display_id}</TableCell>
									<TableCell>{row.type}</TableCell>
									<TableCell>{formatDate(row.actual_date)}</TableCell>
									<TableCell>{formatDate(row.expiry_date)}</TableCell>
									<TableCell>
										<NotificationSentCell row={row} />
									</TableCell>
									<TableCell>
										{row.expired_days >= 0 ? row.expired_days : `in ${-row.expired_days} days`}
									</TableCell>
									<TableCell>
										{row.status === "missed" ? (
											<ConfirmButton
												button={
													<Button size="small" variant="contained" color="warning">
														Send
													</Button>
												}
												title="Send Expiry Notification"
												details={`Notify the sponsors of ESR #${row.display_id} that ${row.type} expired on ${formatDate(row.expiry_date)}?`}
												confirmAction="Send"
												cancelAction="Cancel"
												onConfirm={() => handleSend(row)}
												onCancel={() => {}}
											/>
										) : (
											<Button size="small" onClick={() => navigate(`/esr/${row.root_id}/${row.label}/${row.esr_id}`)}>
												View
											</Button>
										)}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				rowsPerPageOptions={[10, 20, 50]}
				count={data?.total ?? 0}
				rowsPerPage={pageSize}
				page={page}
				onPageChange={(_, p) => setPage(p)}
				onRowsPerPageChange={(e) => {
					setPageSize(parseInt(e.target.value, 10));
					setPage(0);
				}}
			/>
		</Box>
	);
};